import { CommonModule } from '@angular/common';
import { Component, DestroyRef, computed, effect, input, output, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule } from '@ngx-translate/core';
import { StateToAccentColorMap, StateToBackgroundColorMap } from 'dashboard/constants/visuals-dashboard/state-to-colors-maps';
import { VisualsDashboardWidgetSizingEnum } from 'dashboard/enums/visuals/visuals-dashboard-widget-sizing.enum';
import { VisualsDashboardWidgetEnum } from 'dashboard/enums/visuals/visuals-dashboard-widgets.enum';
import { DashboardWidget } from 'dashboard/interfaces/visuals/dashboard-widget.interface';
import { VisualsDashboardService } from 'dashboard/services/visuals-dashboard/visuals-dashboard.service';
import { filter } from 'rxjs';
import { TooltipDirective } from 'shared/directives/tooltip.directive';
import { ModalService } from 'shared/services';
import { VisualsDashboardDistributorPickerComponent } from '../visuals-dashboard-distributor-picker/visuals-dashboard-distributor-picker.component';
import { VisualsDashboardPeriodPickerComponent } from '../visuals-dashboard-period-picker/visuals-dashboard-period-picker.component';

@Component({
    selector: 'app-visual-dashboard-item-wrapper',
    imports: [
        CommonModule,
        TranslateModule,
        TooltipDirective,
        VisualsDashboardPeriodPickerComponent,
        VisualsDashboardDistributorPickerComponent,
    ],
    templateUrl: './visual-dashboard-item-wrapper.component.html',
    styleUrl: './visual-dashboard-item-wrapper.component.scss',
})
export class VisualDashboardItemWrapperComponent {
    removeWidget = output<void>();
    editWidget = output<void>();
    isEditMode = input.required<boolean>();
    widget = input.required<DashboardWidget>();

    private localPeriodOverride = signal<number | null | undefined>(undefined);
    selectedDistributorId = signal<number | null>(null);

    titleTranslationKey = computed(() => {
        const widget = this.widget();
        const widgetId = widget.iceDashboardWidget.widgetTypeId;
        const type = this.visualsDashboardService.widgetTypeIdToEnum()[widgetId];
        const camelCaseType = type.charAt(0).toLowerCase() + type.slice(1);
        return `dashboard.visuals.widgets.${camelCaseType}.title`;
    });

    borderColor = computed(() => {
        const defaultColor = '#ccc';
        const currentOverride = this.currentWidgetStateOverride();
        if (!currentOverride) {
            return defaultColor;
        }
        return StateToAccentColorMap[currentOverride] || defaultColor;
    });

    backgroundColor = computed(() => {
        const defaultColor = '#f9f9f9';
        const currentOverride = this.currentWidgetStateOverride();
        if (!currentOverride) {
            return defaultColor;
        }
        return StateToBackgroundColorMap[currentOverride] || defaultColor;
    });

    currentWidgetStateOverride = computed(() => {
        const htmlId = this.widget().htmlId;
        const overrideMap = this.visualsDashboardService.htmlIdToBackgroundStateOverrideMap();
        return htmlId ? overrideMap[htmlId] : undefined;
    });

    availableDistributors = computed(() => {
        const htmlId = this.widget().htmlId;
        const distributorMap = this.visualsDashboardService.htmlIdToAvailableDistributorsMap();
        if (!htmlId) {
            return [];
        }
        return distributorMap[htmlId] ?? [];
    });

    showDistributorDropdown = computed(() => {
        const widget = this.widget();
        if (
            widget.key !== VisualsDashboardWidgetEnum.ImageTrackerListings &&
            widget.key !== VisualsDashboardWidgetEnum.ImageTrackerPhotos
        ) {
            return false;
        }
        if (widget.selectedSize !== VisualsDashboardWidgetSizingEnum.MEDIUM_ROW) {
            return false;
        }
        const distributors = this.availableDistributors();
        return distributors.length > 0;
    });

    widgetSelectedOptionValue = computed(() => {
        return this.widget().selectedPeriodOptionValue;
    });

    showPeriodPicker = computed(() => {
        return this.widgetSelectedOptionValue() !== null;
    });

    currentPeriodValue = computed(() => {
        const localOverride = this.localPeriodOverride();
        if (localOverride !== undefined) {
            return localOverride;
        }
        return this.widgetSelectedOptionValue();
    });

    widgetInputs = computed(() => {
        const periodValue = this.currentPeriodValue();
        const inputs: { widget: DashboardWidget; pickedPeriodValue?: number | null; selectedDistributorId?: number | null } = {
            widget: this.widget(),
        };
        const selectedDistributorId = this.selectedDistributorId();
        if (periodValue !== null) {
            inputs.pickedPeriodValue = periodValue;
        }
        if (selectedDistributorId !== null) {
            inputs.selectedDistributorId = selectedDistributorId;
        }
        return inputs;
    });

    constructor(
        private visualsDashboardService: VisualsDashboardService,
        private modalService: ModalService,
        private destroyRef: DestroyRef,
    ) {
        effect(() => {
            untracked(() => {
                this.localPeriodOverride.set(undefined);
            });
        });
    }

    onPeriodChange(periodValue: number | null): void {
        this.localPeriodOverride.set(periodValue);
    }

    onDistributorChange(distributorId: number | null): void {
        this.selectedDistributorId.set(distributorId);
    }

    handleEditWidget() {
        this.editWidget.emit();
    }

    handleRemoveWidget() {
        this.modalService
            .openDeleteConfirmationModal(
                'dashboard.visuals.removeWidgetModal.description',
                'dashboard.visuals.removeWidgetModal.title',
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                'center',
                undefined,
                undefined,
                'dashboard.visuals.removeWidgetModal.button',
            )
            .onClose.pipe(takeUntilDestroyed(this.destroyRef), filter(Boolean))
            .subscribe(() => {
                this.removeWidget.emit();
            });
    }
}
